import { Container, Stack, useMediaQuery } from "@mui/material";
import ServicesHeader from "./ServicesHeader/ServicesHeader";
import { useCallback, useContext, useEffect, useState } from "react";
import "./Services.scss";
import TotalEnergiesContext from "../../core/store/context/TotalEnergiesContext";
import ServicesLast from "./ServicesLast/ServicesLast";
import ServicesGeneralList from "./ServicesGeneralList/ServicesGeneralList";
import { getManagements as getManagementsFetch } from "../../core/services/managements/managements.service";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { OwnerDataCups } from "../../core/models/ownerData/ownerData.model";
import {
  FormattedManagementsList,
  GetManagementsRequestBody,
} from "../../core/models/managements/managements.model";
import { formatDateString } from "../../utils/dateUtils/formatDate.util";
import { formatManagementsList } from "../../utils/components/managements/managements.util";
import ServicesHistoric from "./ServicesHistoric/ServicesHistoric";

const Managements = () => {
  const { t } = useTranslation();
  const { ownerData, userData, setManagementsDetailHeader } =
    useContext(TotalEnergiesContext);
  const isDesktopViewport = useMediaQuery("(min-width:1440px)");
  const [selectedTab, setSelectedTab] = useState<string>("managements");
  const [managementsLastList, setManagementsLastList] = useState<
    FormattedManagementsList[]
  >([]);
  const [managementsHistoricList, setManagementsHistoricList] = useState<
    FormattedManagementsList[]
  >([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalRegistersQuery, setTotalRegistersQuery] = useState<number>(0);
  const [isHistoricTableLoading, setIsHistoricTableLoading] =
    useState<boolean>(false);
  const [isLastTableLoading, setIsLastTableLoading] = useState<boolean>(false);
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);

  const getManagements = useCallback(
    async (
      token: string,
      cups: OwnerDataCups[],
      page: number,
      rowsPerPage: number,
      abortSignal: AbortSignal
    ) => {
      const dateTo = new Date(new Date().setDate(new Date().getDate() + 1));
      const dateFrom = new Date(new Date(1, 1, 2000));
      const cifs = [
        ...new Set(cups.filter(({ cif }) => cif.length).map(({ cif }) => cif)),
      ];

      setIsHistoricTableLoading(true);
      setIsLastTableLoading(true);

      const getManagementsRequestBody: GetManagementsRequestBody = {
        Ids: cifs,
        IdTipoRegistro: [7, 8],
        fechaDesde: formatDateString(dateFrom).replace(",", ""),
        fechaHasta: formatDateString(dateTo).replace(",", ""),
        pageSize: rowsPerPage.toString(),
        pageIndex: (page + 1).toString(),
      };
      getManagementsFetch(getManagementsRequestBody, token, abortSignal)
        .then(({ totalRegistersQuery, Result: result }) => {
          setManagementsHistoricList(formatManagementsList(result));
          setTotalRegistersQuery(totalRegistersQuery);
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          setIsHistoricTableLoading(false);
          setIsLastTableLoading(false);
        });
    },
    []
  );

  useEffect(() => {
    const getManagementsAbortController = new AbortController();
    if (userData && ownerData) {
      window.scrollTo({ top: 0, behavior: "auto" });
      setIsHistoricTableLoading(true);
      setManagementsHistoricList([]);
      getManagements(
        userData.token,
        ownerData.cups,
        page,
        rowsPerPage,
        getManagementsAbortController.signal
      );

      return () => getManagementsAbortController.abort();
    }
  }, [getManagements, ownerData, page, rowsPerPage, userData]);

  useEffect(() => {
    if (managementsHistoricList.length && isFirstLoad) {
      setManagementsLastList(managementsHistoricList.slice(0, 3));
      setIsLastTableLoading(false);
      setIsFirstLoad(false);
    }
  }, [isFirstLoad, managementsHistoricList]);

  return (
    <>
      <ServicesHeader
        ownerData={ownerData}
        isDesktopViewport={isDesktopViewport}
        changeTab={(tab: string) => setSelectedTab(tab)}
      />
      <Container component="article">
        {selectedTab === "managements" && (
          <>
            <ServicesLast
              managementsLastList={managementsLastList}
              isTableLoading={isLastTableLoading}
              setManagementsDetailHeader={setManagementsDetailHeader}
            />
            <ServicesGeneralList />
            <Typography component="h3" variant="h3" paddingBottom="1rem">
              {t("Servicios.¿Desea acceder a algún otro servicio?")}
            </Typography>
            <Typography
              component="p"
              variant="body2"
              sx={{ color: "grey.700" }}
              paddingBottom="1.5rem"
            >
              {t(
                "Servicios.Si no encuentra el servicio en esta lista, deberá ponerse en contacto con un asesor comercial"
              )}
            </Typography>
            <Stack
              direction="column"
              gap={"0.75rem"}
              paddingBottom={{ xs: "3.5rem", md: "5rem" }}
            >
              <div className="subtitle brand-gray">
                <Typography variant="body1" className="brand-gray">
                  <span className="t-icon-mail brand-red icon" />
                  {t("Footer.Email contacto")}
                </Typography>
              </div>
              <div className="subtitle brand-gray">
                <Typography variant="body1" className="brand-gray">
                  <span className="t-icon-phone brand-red icon" />
                  {t("Footer.Teléfono contacto")}
                </Typography>
              </div>
            </Stack>
          </>
        )}
        {selectedTab === "historic" && (
          <ServicesHistoric
            managementsHistoricList={managementsHistoricList}
            totalRegistersQuery={totalRegistersQuery}
            isHistoricTableLoading={isHistoricTableLoading}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            setManagementsDetailHeader={setManagementsDetailHeader}
          />
        )}
      </Container>
    </>
  );
};

export default Managements;
